import React, { useEffect } from "react";
import docStyles from "../../css/Docs/Docs.module.css";
import { Divider } from "@mui/material";
import { Link } from "react-router-dom";
import DocWrapper from "./DocWrapper";

const Introduction = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <DocWrapper>
      <div className={docStyles.container}>
        <section id="introduction" className={docStyles.section}>
          <div className={docStyles.heading}>Introduction</div>
          <div className={docStyles.para}>
            EQLFIN is a platform that helps retail buyers generate additional
            income. If you are a B2B application that provides inventory
            solutions, integrate our APIs into your software to help your
            clients earn additional income. In the world of business, vendor
            payments are made on a monthly, weekly, or bi-weekly basis. However,
            businesses generate a steady cash flow throughout the month from
            general consumption, leaving the cash unused for a certain period
            until vendor payments are done on the due date. EQLFIN puts this
            idle cash into work and generates additional income
          </div>
        </section>
        <br />
        <Divider sx={{ width: "100%" }} />
        <section id="definition" className={docStyles.section}>
          <div className={docStyles.heading}>Definitions</div>
          <div className={docStyles.headingTwo}>Office</div>
          <div className={docStyles.para}>
            An office is a collection of invoices and payments created for each
            GST-registered business. The office serves as a central entity for
            adding invoices, updating invoices, and payments. It enables
            businesses to manage their invoices and track payments effectively.
            Each office is uniquely identified by an OfficeId and PAN number
            that allows developers to easily extract office-related data.
          </div>
          <div className={docStyles.para}>
            <div className={docStyles.subHeading}>
              Requirements for Office creation:
            </div>
            An office is created based on the availability of the following
            details:
            <ul
              style={{
                padding: "0px 0px 5px 10px",
                margin: "5px 0px 5px 10px",
              }}
            >
              <li>
                Company GSTIN: The Goods and Services Tax Identification Number
                (GSTIN) of the company associated with the office.
              </li>
              <li>
                First Contact Name: The name of the primary contact person for
                the office.
              </li>
              <li>
                First Contact Email Address: The email address of the primary
                contact person for the office.
              </li>
              <li>
                First Contact Phone Number: The phone number of the primary
                contact person for the office.
              </li>
            </ul>
          </div>

          <div className={docStyles.headingTwo}>Demand</div>
          <div className={docStyles.para}>
            Demand is a GST invoice raised by the vendor against the client on
            some purchase of goods or services.
          </div>

          <div className={docStyles.headingTwo}>Customer/Client</div>
          <div className={docStyles.para}>
            The customer/client is a buyer. A buyer can add or update GRN (Goods
            Received Note) and payments in our system by sharing the Excel sheet
            via email or data can be extracted from the client's ERP systems to
            EQLFIN through API integration.
          </div>

          <div className={docStyles.headingTwo}>Vendor</div>
          <div className={docStyles.para}>
            Vendor is a seller/supplier and can track invoices and payments.
          </div>

          <div className={docStyles.headingTwo}>Activity Id</div>
          <div className={docStyles.para}>
            A unique id which is used to access demands.
          </div>
        </section>

        <br />
        <Divider sx={{ width: "100%" }} />

        <section id="validation" className={docStyles.section}>
          <div className={docStyles.heading}>Validations</div>
          <div className={docStyles.para}>
            These validations help ensure that the data entered in the system
            meets the required standards, reducing errors and maintaining data
            accuracy and consistency.
          </div>
          <ul
            style={{ padding: "0px 0px 5px 10px", margin: "5px 0px 5px 10px" }}
          >
            <li>
              <div className={docStyles.headingTwo}>
                GST (Goods and Services Tax) Validation:
              </div>
              <div className={docStyles.para}>
                The GST validation is performed using a regular expression
                (regex) pattern: <br />
                <b>
                  {" "}
                  /^[0-9]{2}[A-Z]{3}[ABCFGHLJPTF]{1}[A-Z]{1}[0-9]{4}[A-Z]
                  {1}[1-9A-Z]{1}Z[0-9A-Z]{1}/.{" "}
                </b>{" "}
                <br />
                This pattern verifies that a GST number is a 15-digit
                alphanumeric code and matches the required format for GST
                identification.
              </div>
            </li>
            <li>
              <div className={docStyles.headingTwo}>
                Email Address Validation:
              </div>
              <div className={docStyles.para}>
                The email address validation is performed using a regular
                expression (regex) pattern: <br />
                <b>
                  {" "}
                  {`/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<b>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.`}{" "}
                </b>{" "}
                <br />
                This pattern ensures that an email address follows the standard
                format and validates its correctness.
              </div>
            </li>
            <li>
              <div className={docStyles.headingTwo}>
                Phone Number Validation:
              </div>
              <div className={docStyles.para}>
                Phone numbers should be provided with a "+91" prefix to indicate
                the Indian country code. Internally, the system utilizes
                Google's{" "}
                <a href="https://github.com/google/libphonenumber">
                  libphonenumber library{" "}
                </a>
                for validation purposes. This library ensures that phone numbers
                are valid and conform to the correct formatting rules.
              </div>
            </li>
          </ul>
        </section>

        <br />
        <Divider sx={{ width: "100%" }} />

        <section id="authentication" className={docStyles.section}>
          <div className={docStyles.heading}>Authentication</div>
          <div className={docStyles.para}>
            For using the APIs a Baisc Token is required. <br />
            To obtain the Basic Token for accessing the business API, please
            follow the steps outlined below: <br />
            <ul
              style={{
                padding: "0px 0px 5px 10px",
                margin: "5px 0px 5px 10px",
              }}
            >
              <li>
                <div className={docStyles.subHeading}>
                  Manual Office Creation:
                </div>
                The initial step is to create an office manually. This process
                will be handled by our team. Once the office is successfully
                created, we will proceed to the next step.
              </li>
              <li>
                <div className={docStyles.subHeading}>Token Provision:</div>
                After the office creation process, we will provide you with the
                Basic Token. This token serves as an authentication credential
                required to access the business API. It is a unique identifier
                that grants access to the specific office's resources and
                functionality.
              </li>
              <li>
                <div className={docStyles.subHeading}>API Authentication:</div>
                To authenticate your API requests, include the Basic Token in
                the request headers. This will ensure that your requests are
                authorized and processed correctly by the API server. Make sure
                to include the token in the "Authorization" header field when
                making API calls.
              </li>
            </ul>
            <div className={docStyles.infoBlock}>
              Please note that the Basic Token is confidential information and
              should be securely stored and transmitted. Do not share this token
              with unauthorized individuals or expose it publicly.
            </div>
          </div>
        </section>

        <br />
        <Divider sx={{ width: "100%" }} />
        <section id="next-step" className={docStyles.section}>
          <div className={docStyles.heading}>Next Steps</div>
          <div className={docStyles.para}>
            After going through the introduction part of EQLFIN documentation
            you can now head over to different API and start integration them
            into your platform.
          </div>
          <div className={docStyles.apiButtonSection}>
            <Link to="/customer" className={docStyles.apiButton}>
              Customer API
            </Link>
            <Link to="/demand/create" className={docStyles.apiButton}>
              Demand API
            </Link>
            <Link to="/invoice-action" className={docStyles.apiButton}>
              Invoice Action API
            </Link>
          </div>
        </section>
      </div>
      <section className={docStyles.tableOfContentSection}>
        <div className={docStyles.tableOfContent}>
          <div className={docStyles.tocHeading}>Table of contents</div>
          <ul className={docStyles.tocList}>
            <li>
              <Link to="#introduction">Introduction</Link>
            </li>
            <li>
              <Link to="#definition">Definition</Link>
            </li>
            <li>
              <Link to="#validation">Validations</Link>
            </li>
            <li>
              <Link to="#authentication">Authentications</Link>
            </li>
            <li>
              <Link to="#next-step">Next Steps</Link>
            </li>
          </ul>
        </div>
      </section>
    </DocWrapper>
  );
};

export default Introduction;
